import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, AlertController, LoadingController, ModalController, NavController } from '@ionic/angular';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentService } from '../services/payment.service';
import { AvatarService } from '../services/avatar.service';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

declare var Stripe: any;

@Component({
  selector: 'app-add-card',
  templateUrl: './add-card.component.html',
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, ReactiveFormsModule, TranslateModule],
  styles: [`
    .stripe-container { margin-top: 16px; margin-bottom: 24px; }
    .stripe-element {
      padding: 16px;
      border: 2px solid var(--ion-color-step-150, #f4f5f8);
      border-radius: 12px;
      background: #ffffff;
      transition: all 0.3s ease;
    }
    .stripe-element:focus-within {
      border-color: var(--ion-color-primary, #3880ff);
      box-shadow: 0 0 0 4px rgba(56, 128, 255, 0.1);
    }
    .card-errors { color: var(--ion-color-danger, #eb445a); font-size: 13px; margin-top: 8px; min-height: 20px; }
    .add-card-btn { margin-top: 24px; --border-radius: 12px; height: 50px; font-weight: 600; text-transform: none; }
    .section-header { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
    .section-header h2 { margin: 0; font-size: 20px; font-weight: 600; color: var(--ion-text-color, #000); }
  `]
})
export class AddCardComponent {
  @ViewChild('cardElement', { static: false }) cardElement: ElementRef;
  paymentForm: FormGroup;
  loading: HTMLIonLoadingElement;
  stripe: any;
  elements: any;
  card: any;
  isCardComplete: boolean = false;
  cardInitialized: boolean = false;

  constructor(
    private fb: FormBuilder,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private paymentService: PaymentService,
    private avatarService: AvatarService,
    private modalController: ModalController,
    private navController: NavController
  ) {
    this.paymentForm = this.fb.group({
      provider: ['stripe', Validators.required],
      cardId: [''],
      authCode: ['']
    });
  }

  ngOnInit() {
    // Initialize the form or any other needed data
  }

  ngAfterViewInit() {
    this.initializeStripeCard();
  }

  initializeStripeCard() {
    if (this.cardElement && this.cardElement.nativeElement) {
      this.stripe = Stripe(environment.stripePublishableKey);
      this.elements = this.stripe.elements();

      const style = {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      };

      if (!this.card) {
        this.card = this.elements.create('card', { style: style });
        this.card.mount(this.cardElement.nativeElement);
        this.card.on('change', event => {
          this.isCardComplete = event.complete;
        });
      }
      this.cardInitialized = true;
    }
  }

  async processPayment() {
    if (this.paymentForm.valid) {
      const formValues = this.paymentForm.value;
      formValues.email = this.avatarService.user.email; // Use email from avatarService

      this.showLoading();

      try {
        const cardData = await this.processStripePayment(formValues);

        await this.modalController.dismiss({ success: true, cardData });
      } catch (error) {
        const errorMessage = error.message || (error.error ? error.error.error : 'An unexpected error occurred.');
        await this.showAlert('Error', errorMessage);
      } finally {
        this.hideLoading();
      }
    } else {
      this.showAlert('Form Error', 'Please fill out all required fields.');
    }
  }

  async processStripePayment(formValues) {
    console.log('Starting processStripePayment with formValues:', formValues);

    try {
      const setupIntentResponse = await this.paymentService.createSetupIntent(formValues.email).toPromise();
      console.log('Setup Intent raw response:', setupIntentResponse);
      const resp: any = setupIntentResponse;
      const clientSecret = resp && (resp.client_secret || resp.clientSecret || resp.clientSecretValue || resp.secret);
      console.log('Resolved clientSecret:', clientSecret);

      if (!clientSecret) {
        throw new Error('No client_secret returned from server for SetupIntent.');
      }

      if (String(clientSecret).startsWith('seti_') && !String(clientSecret).includes('_secret_')) {
        throw new Error('Invalid client_secret returned from server (looks like an ID). Ensure server returns the full client_secret.');
      }

      const { setupIntent, error } = await this.stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: this.card,
            billing_details: {
              email: formValues.email,
            },
          },
        }
      );

      if (error) {
        console.error('Stripe confirmCardSetup returned error object:', error);
        throw new Error(error.message);
      }

      console.log('Card setup confirmed:', setupIntent);

      const paymentMethodId = setupIntent.payment_method;

      // Fetch the payment method details from your server (which will call Stripe)
      const paymentMethod = await this.paymentService.retrievePaymentMethod(paymentMethodId).toPromise();
      console.log('Payment method retrieved:', paymentMethod);

      const cardDetails = paymentMethod.card;
      const last4 = cardDetails.last4;
      const brand = cardDetails.brand; // Get card brand (visa, mastercard, etc.)

      console.log('Checking if card exists with email:', formValues.email, ' and last4:', last4);
      const cardExists = await this.avatarService.checkCardExistsStripe(formValues.email, last4);

      if (cardExists) {
        throw new Error('This card is already saved to your account.');
      }

      // Save card to Firestore using the correct method
      const cardData = {
        cardId: paymentMethodId,
        email: formValues.email,
        last4: last4,
        brand: brand || 'unknown'
      };

      await this.avatarService.saveCard(cardData);
      console.log('Card saved to Firestore:', cardData);

      // Also save to backend if needed
      await this.paymentService.savePaymentMethod(formValues.email, paymentMethodId).toPromise();

      return cardData;

    } catch (error) {
      console.error('Error in processStripePayment:', error);
      throw error;
    }
  }

  async showLoading() {
    this.loading = await this.loadingController.create({ message: 'Processing payment...' });
    await this.loading.present();
  }

  async hideLoading() {
    if (this.loading) {
      await this.loading.dismiss();
    }
  }

  async showAlert(header: string, message: string) {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: ['OK'],
    });
    await alert.present();
  }

  closeModal() {
    this.modalController.dismiss();
  }
}
